import { InteractiveMapBlockConfiguration, InteractiveMapBlockElements } from '@features/Components/InteractiveMapBlock/Scripts/interactiveMapBlock.types';
import useInfoPointDirection from '@features/Components/InteractiveMapBlock/Scripts/useInfoPointDirection';
import useInfoPointPlacement from '@features/Components/InteractiveMapBlock/Scripts/useInfoPointPlacement';
import useInteractiveMapInteractions from '@features/Components/InteractiveMapBlock/Scripts/useInteractiveMapInteractions';
import useInteractiveMapState from '@features/Components/InteractiveMapBlock/Scripts/useInteractiveMapState';
import useMultipleFloors from '@features/Components/InteractiveMapBlock/Scripts/useMultipleFloors';
import usePanZoom from '@features/Components/InteractiveMapBlock/Scripts/usePanZoom';

const blockClass = 'interactive-map-block';

const isEnabledDatasetFlag = (datasetValue: string | undefined): boolean => {
    return datasetValue === 'True' || datasetValue === 'true';
};

const getBlockConfiguration = (blockContainerElement: HTMLDivElement): InteractiveMapBlockConfiguration => {
    return {
        blockClass,
        site: blockContainerElement.dataset.site || '',
        language: blockContainerElement.dataset.language || 'sv',
        hasMultipleFloors: isEnabledDatasetFlag(blockContainerElement.dataset.hasMultipleFloors),
        hasPanZoom: isEnabledDatasetFlag(blockContainerElement.dataset.hasPanzoom),
        hasFlippedInfoPoints: isEnabledDatasetFlag(blockContainerElement.dataset.hasFlippedInfoPoints),
        useViewportAsLimiter: isEnabledDatasetFlag(blockContainerElement.dataset.useViewportAsLimiter),
    };
};

const getInteractiveElements = (blockElement: HTMLElement): InteractiveMapBlockElements | null => {
    const blockContainerElement = blockElement.querySelector<HTMLDivElement>(`.${blockClass}__container`);
    const mapElement = blockElement.querySelector<HTMLDivElement>(`.${blockClass}__map`);

    if (!blockContainerElement || !mapElement) {
        return null;
    }

    return {
        blockContainerElement,
        infoListButtonElements: Array.from(blockElement.querySelectorAll<HTMLButtonElement>(`.${blockClass}__info-list-item-button`)),
        infoListItemElements: Array.from(blockElement.querySelectorAll<HTMLLIElement>(`.${blockClass}__info-list-item`)),
        infoPointElements: Array.from(blockElement.querySelectorAll<HTMLDivElement>(`.${blockClass}__info-point`)),
        mapElement,
    };
};

const getInteractionScopeElement = (blockElement: HTMLElement): HTMLElement => {
    return blockElement.closest<HTMLElement>(`.${blockClass}__container--multiple-floors`) ?? blockElement;
};

const initializeInteractiveMapBlock = (blockElement: HTMLElement): void => {
    const interactiveElements = getInteractiveElements(blockElement);

    if (!interactiveElements) {
        return;
    }

    const configuration = getBlockConfiguration(interactiveElements.blockContainerElement);
    const interactionScopeElement = getInteractionScopeElement(blockElement);

    useInfoPointPlacement({
        infoPointElements: interactiveElements.infoPointElements,
    });

    const state = useInteractiveMapState({
        blockClass,
        blockElement,
        interactiveElements,
        interactionScopeElement,
    });

    const directionController = useInfoPointDirection({
        blockClass,
        blockElement,
        hasFlippedInfoPoints: configuration.hasFlippedInfoPoints,
        mapElement: interactiveElements.mapElement,
        useViewportAsLimiter: configuration.useViewportAsLimiter,
    });

    const panZoomController = configuration.hasPanZoom
        ? usePanZoom({
            blockClass,
            mapElement: interactiveElements.mapElement,
        })
        : {
            isActivationSuppressed: (): boolean => false,
        };

    useInteractiveMapInteractions({
        configuration,
        directionController,
        interactiveElements,
        panZoomController,
        state,
    });
};

document.addEventListener('DOMContentLoaded', () => {
    const blockElements = document.querySelectorAll<HTMLElement>(`.${blockClass}`);

    if (blockElements.length === 0) {
        return;
    }

    const firstBlockContainerElement = blockElements[0].querySelector<HTMLDivElement>(`.${blockClass}__container`);

    if (!firstBlockContainerElement) {
        return;
    }

    const firstBlockContainerConfiguration = getBlockConfiguration(firstBlockContainerElement);
    
    // TODO: Handle floors within the Interactive Map Block
    // As it is now, we are creating floors from multiple InteractiveMapBlock instances
    // on a page. Max 2 blocks per page are currently supported.
    if (firstBlockContainerConfiguration.hasMultipleFloors) {
        useMultipleFloors({
            blockClass,
            blockElements,
            language: firstBlockContainerConfiguration.language,
            site: firstBlockContainerConfiguration.site,
        });
    }

    blockElements.forEach((blockElement) => {
        initializeInteractiveMapBlock(blockElement);
    });
});
